/**
 * Contract ladder helpers (Oct 2026): the analysis page's picks and its
 * all-contracts browser render one table.
 *
 * Gold phase 2b: every figure (DTE, OTM %, yield, credit, earnings inside,
 * monthly / weekly, THIN, tiny premium, staleness, best contract) comes from
 * the backend (gold.v_option_chain); these helpers only sort and format.
 * A missing value is null and the table shows "—".
 */
import type { ChainContractFacts } from '../types';

/** Fields both StrategyContract and BrowseContract carry. */
export interface LadderContract extends ChainContractFacts {
  strike_price: number;
  expiration_date: string;
  days_to_expiration: number | null;
  delta: number | null;
  bid: number | null;   // null until a quote feed exists
  ask: number | null;
  est_bid?: number | null;  // estimate, shown "est."
  est_ask?: number | null;
  open_interest: number | null;
  implied_volatility: number | null;
  premium: number | null;
  spread_load: number | null;  // contract spread (quote), else the stock-level spread; null = unknown
  aroc: number | null;
  downside_protection: number | null;
  viability_warning: string | null;
  last_trade_at?: string | null;
  is_stale?: boolean | null;
  contract_ticker?: string;
  theta?: number | null;
  volume?: number | null;
}

export type LadderSortKey = 'expiry' | 'strike' | 'dte' | 'otm' | 'delta' | 'premium' | 'aroc' | 'protection' | 'iv' | 'oi' | 'spread';

const num = (v: number | null | undefined) => (v == null ? -Infinity : v);

export function sortContracts<T extends LadderContract>(rows: T[], key: LadderSortKey, dir: 1 | -1): T[] {
  const v = (c: T): number => {
    switch (key) {
      case 'expiry': return Number(c.expiration_date.slice(0, 10).replace(/-/g, ''));
      case 'strike': return c.strike_price;
      case 'dte': return num(c.days_to_expiration);
      case 'otm': return num(c.otm_pct);
      case 'delta': return c.delta == null ? -Infinity : Math.abs(c.delta);
      case 'premium': return num(c.premium);
      case 'aroc': return num(c.aroc);
      case 'protection': return num(c.downside_protection);
      case 'iv': return num(c.implied_volatility);
      case 'oi': return num(c.open_interest);
      case 'spread': return num(c.spread_load);
    }
  };
  // ties (same expiry, same value) fall back to strike, ascending
  return [...rows].sort((a, b) => {
    const va = v(a), vb = v(b);
    const d = va === vb ? 0 : va === -Infinity ? -1 : vb === -Infinity ? 1 : va - vb;
    return d * dir || a.strike_price - b.strike_price;
  });
}
